import React, { useState, lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, Phone, MapPin, Clock, CheckCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';
const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      // EmailJS configuration for sending to admin
      const serviceId = 'service_93e13ll';
      const templateId = 'template_f5qtc8e'; // Contact Us template
      const publicKey = 'SgnDoSgHGRyOY5b_9'; // Your public key
      const templateParams = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        time: new Date().toLocaleString() // Adding timestamp
      };
      // Send the email to admin
      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      // Send confirmation email to the user
      const autoReplyTemplateId = 'template_f5qtc8e'; // You may need to create a separate auto-reply template
      const autoReplyParams = {
        to_name: formData.name,
        to_email: formData.email,
        message: "Thank you for contacting Northampton Driving School. We've received your message and will get back to you shortly."
      };
      // Send auto-reply email to the user
      await emailjs.send(serviceId, autoReplyTemplateId, autoReplyParams, publicKey);
      console.log('Emails sent successfully!');
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (err) {
      console.error('Failed to send email:', err);
      setError('Failed to send your message. Please try again or contact us directly at info@northamptondrivingschool.co.uk');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="bg-white" data-id="element-1161" data-mp="3pjzsvebh4fyn">
      <Helmet data-id="element-1162" data-mp="3plt2n2hdvgav">
        <title data-id="element-1163" data-mp="3pl9qy3ibto5a">
          Contact Northampton Driving School | Book Driving Lessons Today
        </title>
        <meta name="description" content="Get in touch to book automatic driving lessons in Northampton. Find answers to common questions and start your driving journey with us." data-id="element-1164" data-mp="3ulp3isusarm7" />
        <meta name="keywords" content="book driving lessons Northampton, contact driving instructor, automatic driving lessons booking, driving lesson enquiry Northampton" data-id="element-1165" data-mp="3krez2cski2v4" />
      </Helmet>
      <div className="container mx-auto px-4 py-12" data-id="element-1166" data-mp="21v3igf3uqqyk">
        <h1 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1167" data-mp="21tisl2shfw71">Contact Us</h1>
        <h2 className="sr-only" data-id="element-1168" data-mp="1wuz3itile4ag">Book Driving Lessons in Northampton</h2>
        <h2 className="sr-only" data-id="element-1169" data-mp="2bkn6s0ac3d0t">Contact Northampton Driving Instructors</h2>
        <h2 className="sr-only" data-id="element-1170" data-mp="2bmr76890xtwc">Enquire About Automatic Driving Lessons</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-1171" data-mp="26mbvdl4e4ugh">
          <div data-id="element-1172" data-mp="1ivfxc69cd30s">
            <p className="text-gray-700 mb-6" data-id="element-1173" data-mp="1ix0pxqc3ibw0">
              We're here to answer any questions you may have about our driving
              lessons. Fill out the form and we'll get back to you as soon as
              possible.
            </p>
            {isSubmitted ? <div className="bg-green-100 border-2 border-green-300 text-green-800 p-6 rounded-lg mb-6 shadow-md" data-id="element-1174" data-mp="1nusr7aymracv">
                <div className="flex items-center mb-4" data-id="element-1175" data-mp="348j1izgceuuy">
                  <CheckCircle size={24} className="text-green-600 mr-2" data-id="element-1176" data-mp="2zahgi8zcwlgu" />
                  <h3 className="text-xl font-semibold" data-id="element-1177" data-mp="2zcljmopflgg2">Thank You!</h3>
                </div>
                <p className="mb-3" data-id="element-1178" data-mp="18v84erwy36og">
                  Your message has been sent successfully. We'll get back to you
                  shortly.
                </p>
                <p className="text-sm" data-id="element-1179" data-mp="1ht0n861vkwgl">
                  A confirmation email has been sent to your email address.
                </p>
                <button onClick={() => setIsSubmitted(false)} className="mt-4 bg-burgundy-600 hover:bg-burgundy-700 text-white px-4 py-2 rounded-md font-medium transition-colors" data-id="element-1180" data-mp="1mug5jqco4ova">
                  Send Another Message
                </button>
              </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1181" data-mp="25jeis6jo5dfx">
                <div data-id="element-1182" data-mp="25lqw26ehezmn">
                  <label htmlFor="name" className="block text-gray-700 font-medium mb-1" data-id="element-1183" data-mp="2air5mrajikii">
                    Name
                  </label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-burgundy-500" aria-required="true" data-id="element-1184" data-mp="2plr5vgmyt5ck" />
                </div>
                <div data-id="element-1185" data-mp="38bqqnrbrvya6">
                  <label htmlFor="email" className="block text-gray-700 font-medium mb-1" data-id="element-1186" data-mp="38ecubfvjltn4">
                    Email
                  </label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-burgundy-500" aria-required="true" data-id="element-1187" data-mp="2yk2sl7kpnizq" />
                </div>
                <div data-id="element-1188" data-mp="1pc9a1jcw4034">
                  <label htmlFor="phone" className="block text-gray-700 font-medium mb-1" data-id="element-1189" data-mp="1pcsrkyymomgm">
                    Phone
                  </label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-burgundy-500" aria-required="true" data-id="element-1190" data-mp="1fly8u4klc4zx" />
                </div>
                <div data-id="element-1191" data-mp="2m3lcc9wib2oq">
                  <label htmlFor="message" className="block text-gray-700 font-medium mb-1" data-id="element-1192" data-mp="2m54oppf1ktl8">
                    Message
                  </label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-burgundy-500" aria-required="true" data-id="element-1193" data-mp="fg43exknz60z"></textarea>
                </div>
                {error && <div className="bg-red-100 text-red-800 p-3 rounded-md" role="alert" data-id="element-1194" data-mp="u47cifu06xu0">
                    {error}
                  </div>}
                <button type="submit" disabled={isLoading} className={`bg-burgundy-600 hover:bg-burgundy-700 text-white px-6 py-3 rounded-md font-medium transition-colors ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`} aria-label="Submit contact form" data-id="element-1195" data-mp="1332m81w9aq22">
                  {isLoading ? 'Sending...' : 'Send Message'}
                </button>
              </form>}
          </div>
          <div data-id="element-1196" data-mp="2stxtsjgurvk7">
            <div className="bg-gray-50 p-6 rounded-lg mb-6" data-id="element-1197" data-mp="2svid1cxpvs2r">
              <h2 className="text-xl font-bold mb-4" data-id="element-1198" data-mp="2xqxpq1jznhxl">Contact Information</h2>
              <ul className="space-y-4" data-id="element-1199" data-mp="205a3g9h8yfzs">
                <li className="flex items-start" data-id="element-1200" data-mp="206be4cucp0gz">
                  <Phone size={20} className="text-burgundy-600 mr-4 mt-1" aria-hidden="true" data-id="element-1201" data-mp="2531vd3hw623z" />
                  <div data-id="element-1202" data-mp="2f1re0yhm3qla">
                    <p className="font-medium" data-id="element-1203" data-mp="2f2amzppab4n6">Phone</p>
                    <a href="tel:07481300600" className="text-burgundy-600 hover:text-burgundy-800" aria-label="Call us at 07481 300 600" data-id="element-1204" data-mp="2ezoftr6b2r5h">
                      07481 300 600
                    </a>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1205" data-mp="19uq7gba1sqon">
                  <Mail size={20} className="text-burgundy-600 mr-4 mt-1" aria-hidden="true" data-id="element-1206" data-mp="19uzwnwn9qtta" />
                  <div data-id="element-1207" data-mp="103n5765du5ns">
                    <p className="font-medium" data-id="element-1208" data-mp="104582lmjvi4o">Email</p>
                    <a href="mailto:info@northamptondrivingschool.co.uk" className="text-burgundy-600 hover:text-burgundy-800" aria-label="Email us at info@northamptondrivingschool.co.uk" data-id="element-1209" data-mp="v63g1b75c8h2">
                      info@northamptondrivingschool.co.uk
                    </a>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1210" data-mp="2gjg91ongi1fc">
                  <MapPin size={20} className="text-burgundy-600 mr-4 mt-1" aria-hidden="true" data-id="element-1211" data-mp="2gi55npxxavor" />
                  <div data-id="element-1212" data-mp="xe3rsxf2gl8v">
                    <p className="font-medium" data-id="element-1213" data-mp="xdkclogswayj">Service Area</p>
                    <p data-id="element-1214" data-mp="sf90f7319od5">
                      <span className="text-burgundy-600" data-id="element-1215" data-mp="sfsluxrw6uvx">N</span>orthampton and
                      surrounding areas
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg" data-id="element-1216" data-mp="12cnfb29b653c">
              <h2 className="text-xl font-bold mb-4" data-id="element-1217" data-mp="epyqdwlvtcd8">Operating Hours</h2>
              <ul className="space-y-2" data-id="element-1218" data-mp="9qcg9w1coc8n">
                <li className="flex items-center" data-id="element-1219" data-mp="9rdta61xf8t0">
                  <Clock size={16} className="text-burgundy-600 mr-3" aria-hidden="true" data-id="element-1220" data-mp="ngz6t0wyivtn" />
                  <span data-id="element-1221" data-mp="ioefq75fqwtt">Monday - Thursday: 9:15 AM - 7:00 PM</span>
                </li>
                <li className="flex items-center" data-id="element-1222" data-mp="3r4hnecwjsli5">
                  <Clock size={16} className="text-burgundy-600 mr-3" aria-hidden="true" data-id="element-1223" data-mp="3n5ij8uin4srr" />
                  <span data-id="element-1224" data-mp="3hrihlngt31bd">Friday: Closed</span>
                </li>
                <li className="flex items-center" data-id="element-1225" data-mp="3ht1xve2gpcd5">
                  <Clock size={16} className="text-burgundy-600 mr-3" aria-hidden="true" data-id="element-1226" data-mp="332umfiavgtly" />
                  <span data-id="element-1227" data-mp="37zl1c2axxj5s">Saturday - Sunday: 9:15 AM - 7:00 PM</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default ContactPage;